/**
 * The landing page's objection-handling FAQ.
 *
 * The six objections that block a NON-DEVELOPER buyer, in the order they occur
 * to them. "Do I need a developer?" is first because it is the first thing a VP
 * Product wants to know and the old page never answered it — it simply assumed
 * the reader was the developer.
 *
 * **Why the copy is duplicated, and why that is safe.** These answers are the
 * same text as `docs.faq.q.*`. The obvious move is to render the docs keys
 * directly, and the first version of this file did — but the i18n catalog is
 * split per group (ADR-0195) and `/` loads ONLY `core`, while `docs.*` lives in
 * the `docs` group. Rendering docs keys here made the landing print raw key
 * strings in production; the unit tests passed anyway because the test
 * translator loads the whole catalog. Pulling the `docs` group into the landing
 * to fix it would add ~320 KB for six paragraphs.
 *
 * So the copy lives under `landing.trust.faq.*` (which lands in `core`), and
 * `faq-parity.test.ts` asserts every entry is byte-identical to its `docs.faq`
 * counterpart **in all six locales**. The section's lead promises "the same
 * answers as our documentation"; the guard is what makes that a fact rather
 * than a claim, and it fails the build the moment either side is edited alone.
 */

export interface LandingFaqEntry {
  /** i18n key for the question, in the `core`-group landing namespace. */
  readonly questionKey: string;
  /** i18n key for the answer. */
  readonly answerKey: string;
  /** The `docs.faq` entry this must stay identical to. */
  readonly docsId: string;
}

function faq(id: string): LandingFaqEntry {
  return {
    questionKey: `landing.trust.faq.${id}.q`,
    answerKey: `landing.trust.faq.${id}.a`,
    docsId: id,
  };
}

export const LANDING_FAQ: readonly LandingFaqEntry[] = [
  faq('needDeveloper'),
  faq('howLong'),
  faq('exceedPermissions'),
  faq('tricked'),
  faq('trainAi'),
  faq('cost'),
] as const;

/** Where the reader goes for the other thirteen answers. */
export const FAQ_MORE_HREF = '/docs/faq';
